import { ApiError } from "@airange/shared/api";
import { eduAdminOpenApiClient } from "../generated/client";
import type { operations } from "@airange/generated-contracts/edu";
import { omitEmptyQuery } from "./openapi-query";
import { unwrapOpenApiResult, unwrapOpenApiVoidResult } from "./openapi-result";

type OperationResponse<
  TOperation extends { responses: Record<number, unknown> },
  TStatus extends keyof TOperation["responses"],
> = TOperation["responses"][TStatus] extends {
  content: { "application/json": infer TResponse };
}
  ? TResponse
  : never;

type OperationRequestBody<TOperation extends { requestBody?: unknown }> =
  NonNullable<TOperation["requestBody"]> extends {
    content: { "application/json": infer TBody };
  }
    ? TBody
    : never;

type OperationQuery<TOperation extends { parameters: { query?: unknown } }> = NonNullable<
  TOperation["parameters"]["query"]
>;

type OperationPath<TOperation extends { parameters: { path?: unknown } }> = NonNullable<
  TOperation["parameters"]["path"]
>;

type OperationNoContentResponse<
  TOperation extends { responses: Record<number, unknown> },
  TStatus extends keyof TOperation["responses"],
> = TOperation["responses"][TStatus] extends { content?: never } ? void : never;

type UiNonNullQuery<TQuery extends object, TReplaced extends keyof TQuery = never> = {
  [TKey in Exclude<keyof TQuery, TReplaced>]?: NonNullable<TQuery[TKey]>;
};

type GetMembersOperation = operations["get_members_api_members_get"];
type GetMembersOverviewOperation = operations["get_members_overview_api_members_overview_get"];
type GetMemberDetailOperation = operations["get_member_api_members__member_id__get"];
type CreateMembersOperation = operations["create_members_api_members_post"];
type RemoveMemberOperation = operations["remove_member_api_members__member_id__delete"];
type UpdateMemberRolesOperation = operations["put_member_roles_api_members__member_id__roles_put"];
type DirectoryPickerBootstrapOperation =
  operations["get_identity_directory_picker_bootstrap_api_platform_identity_directory_picker_bootstrap_get"];
type DirectoryPickerUserSearchOperation =
  operations["search_identity_directory_picker_users_api_platform_identity_directory_picker_users_search_post"];
type GetMembersGeneratedQuery = OperationQuery<GetMembersOperation>;
type GetMemberId = OperationPath<GetMemberDetailOperation>["member_id"];
type RemoveMemberId = OperationPath<RemoveMemberOperation>["member_id"];
type UpdateMemberRolesId = OperationPath<UpdateMemberRolesOperation>["member_id"];

export type EduMemberListQuery = UiNonNullQuery<GetMembersGeneratedQuery, "page_size"> & {
  pageSize?: NonNullable<GetMembersGeneratedQuery["page_size"]>;
};

export type EduMembersOverview = OperationResponse<GetMembersOverviewOperation, 200>;
export type EduMemberOverviewBucket = EduMembersOverview["buckets"][number];
export type EduMembersListResponse = OperationResponse<GetMembersOperation, 200>;
export type EduMemberListItem = EduMembersListResponse["items"][number];
export type EduMemberDetail = OperationResponse<GetMemberDetailOperation, 200>;
export type EduMemberAssignment = EduMemberDetail["assignments"][number];
export type EduMemberBulkCreatePayload = OperationRequestBody<CreateMembersOperation>;
export type EduMemberCreateItem = EduMemberBulkCreatePayload["items"][number];
export type EduMemberBulkCreateResponse = OperationResponse<CreateMembersOperation, 201>;
export type EduMemberManageRecord = OperationResponse<UpdateMemberRolesOperation, 200>;
export type EduMemberRoleUpdatePayload = OperationRequestBody<UpdateMemberRolesOperation>;
export type EduMemberRoleKey = EduMemberRoleUpdatePayload["role_keys"][number];
export type EduMemberRoleSource = Pick<EduMemberDetail, "role_keys">;
type EduMemberRemoveResponse = OperationNoContentResponse<RemoveMemberOperation, 204>;
export type EduDirectoryPickerBootstrap = OperationResponse<DirectoryPickerBootstrapOperation, 200>;
export type EduDirectoryPickerUserSearchQuery =
  OperationRequestBody<DirectoryPickerUserSearchOperation>;
export type EduDirectoryPickerUserSearchResponse = OperationResponse<
  DirectoryPickerUserSearchOperation,
  200
>;

const EDU_MEMBER_ROLE_KEYS = [
  "edu_admin",
  "edu_manager",
  "edu_teacher",
  "edu_assistant",
  "edu_student",
] as const satisfies readonly EduMemberRoleKey[];
const EDU_MEMBER_ROLE_KEY_SET = new Set<string>(EDU_MEMBER_ROLE_KEYS);

function isEduMemberRoleKey(value: unknown): value is EduMemberRoleKey {
  return typeof value === "string" && EDU_MEMBER_ROLE_KEY_SET.has(value);
}

export function readEduMemberRoleKeys(
  member: EduMemberRoleSource,
  path = "member.role_keys",
): EduMemberRoleKey[] {
  const roleKeys = member.role_keys;
  if (!Array.isArray(roleKeys)) {
    throw new ApiError(500, {
      code: "edu.member.data_contract_invalid",
      message: `${path} must be an array`,
    });
  }
  const invalidIndex = roleKeys.findIndex((roleKey) => !isEduMemberRoleKey(roleKey));
  if (invalidIndex >= 0) {
    throw new ApiError(500, {
      code: "edu.member.data_contract_invalid",
      message: `${path}[${invalidIndex}] must be a canonical EDU member role_key`,
    });
  }
  return roleKeys;
}

function readEduMemberDetail(member: EduMemberDetail): EduMemberDetail {
  readEduMemberRoleKeys(member);
  return member;
}

function readEduMemberManageRecord(
  member: EduMemberManageRecord,
  path = "member.role_keys",
): EduMemberManageRecord {
  readEduMemberRoleKeys(member, path);
  return member;
}

function readEduMemberManageRecords(
  members: EduMemberBulkCreateResponse,
  path: string,
): EduMemberBulkCreateResponse {
  members.forEach((member, index) => {
    readEduMemberManageRecord(member, `${path}[${index}].role_keys`);
  });
  return members;
}

function toMemberListQueryParams(query?: EduMemberListQuery): GetMembersGeneratedQuery | undefined {
  if (!query) return undefined;
  const { pageSize, ...generatedQuery } = query;
  return omitEmptyQuery<GetMembersGeneratedQuery>({
    ...generatedQuery,
    ...(pageSize === undefined ? {} : { page_size: pageSize }),
  });
}

const eduMembersApi = {
  async getMembersOverview(): Promise<EduMembersOverview> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/members/overview"));
  },
  async getMembers(query?: EduMemberListQuery): Promise<EduMembersListResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/members", {
        params: { query: toMemberListQueryParams(query) },
      }),
    );
  },
  async createMembers(payload: EduMemberBulkCreatePayload): Promise<EduMemberBulkCreateResponse> {
    const response = await unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/members", { body: payload }),
    );
    return readEduMemberManageRecords(response, "created_members");
  },
  async getMemberDetail(memberId: GetMemberId): Promise<EduMemberDetail> {
    const response = await unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/members/{member_id}", {
        params: { path: { member_id: memberId } },
      }),
    );
    return readEduMemberDetail(response);
  },
  async updateMemberRoles(
    memberId: UpdateMemberRolesId,
    payload: EduMemberRoleUpdatePayload,
  ): Promise<EduMemberManageRecord> {
    const response = await unwrapOpenApiResult(
      await eduAdminOpenApiClient.PUT("/api/members/{member_id}/roles", {
        params: { path: { member_id: memberId } },
        body: payload,
      }),
    );
    return readEduMemberManageRecord(response);
  },
  async deleteMember(memberId: RemoveMemberId): Promise<EduMemberRemoveResponse> {
    return unwrapOpenApiVoidResult(
      await eduAdminOpenApiClient.DELETE("/api/members/{member_id}", {
        params: { path: { member_id: memberId } },
      }),
    );
  },
  async loadDirectoryPickerBootstrap(): Promise<EduDirectoryPickerBootstrap> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/platform/identity/directory-picker/bootstrap"),
    );
  },
  async searchDirectoryPickerUsers(
    query: EduDirectoryPickerUserSearchQuery,
  ): Promise<EduDirectoryPickerUserSearchResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/platform/identity/directory-picker/users/search", {
        body: query,
      }),
    );
  },
};

export const __eduMembersApiTest__ = {
  readEduMemberDetail,
  readEduMemberManageRecord,
  readEduMemberManageRecords,
  readEduMemberRoleKeys,
};

export default eduMembersApi;
